import { Box, Stack, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import { SubpageLayout } from '@/components/architecture/Layout/SubpageLayout';
import { BigBreadcrumbs } from '@/components/navigation/BigBreadcrumbs';
import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { useRequiredParam } from '@/hooks/useRequiredParam';
import { DOCUMENT_TYPE_DISPLAY_NAMES } from '@/modules/documents/documents.constants';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { DocumentType } from '@/types/schema';

import { PageActions } from './components/PageActions';
import { PreviewPanel } from './components/PreviewPanel/PreviewPanel';
import { PromptEditorPanel } from './components/PromptEditorPanel/PromptEditorPanel';
import { EMPTY_PROMPT_SENTINEL } from './constants';
import {
  AdminPromptIterationPage_DocumentSummaryPromptFragment,
  useAdminPromptIterationPageQuery,
  useCreateDocumentSummaryPromptMutation,
  useDeleteDocumentSummaryPromptMutation,
  useImprovePromptStreamingSubscription,
  useUpdateDocumentSummaryPromptMutation,
} from './graphql/AdminPromptIterationPage.generated';
import { usePreviewRunner } from './hooks/domain/usePreviewRunner';
import { ImprovePromptModal } from './ImprovePromptModal';
import { PromptDiffModal } from './PromptDiffModal';

const useStyles = makeStyles()(() => ({
  childrenWrapper: {
    position: 'relative',
  },
}));

export function AdminPromptIterationPage() {
  const promptId = useRequiredParam('id');
  const { showFeedback } = useFeedback();
  const { data, loading } = useAdminPromptIterationPageQuery({
    variables: { id: promptId },
    onError: () => {
      showFeedback(
        "We weren't able to load the prompt. Please refresh the page and try again."
      );
    },
  });

  const originalPrompt =
    data?.node?.__typename === 'DocumentSummaryPrompt' ? data.node : null;

  if (loading || !originalPrompt) {
    return (
      <SubpageLayout
        heading={
          <BigBreadcrumbs
            crumbs={[
              {
                label: 'Summary prompts',
                to: getCompletePathFromRouteKey(
                  ROUTE_KEYS.ADMIN_SUMMARY_PROMPTS,
                  {}
                ),
              },
              { label: originalPrompt?.displayName ?? 'Loading...' },
            ]}
          />
        }
      >
        <Typography variant="body-s">Loading...</Typography>
      </SubpageLayout>
    );
  }

  // Mount with saved content. The editor only reads its initial value on mount.
  return <PromptIterationForm key={promptId} originalPrompt={originalPrompt} />;
}

function PromptIterationForm({
  originalPrompt,
}: {
  originalPrompt: AdminPromptIterationPage_DocumentSummaryPromptFragment;
}) {
  const promptId = originalPrompt.id;
  const { showFeedback } = useFeedback();
  const { navigate } = useNavigateToRoute();
  const { reportError } = useReportError();
  const { classes } = useStyles();

  const [promptMarkdown, setPromptMarkdown] = useState(() => {
    const markdown = originalPrompt.promptMarkdown ?? '';
    return markdown === EMPTY_PROMPT_SENTINEL ? '' : markdown;
  });
  const [displayName, setDisplayName] = useState(originalPrompt.displayName);
  const [isImprovePromptModalOpen, setIsImprovePromptModalOpen] =
    useState(false);
  const [suggestedMarkdown, setSuggestedMarkdown] = useState<string | null>(
    null
  );
  const [isDiffModalOpen, setIsDiffModalOpen] = useState(false);
  const [editorVersion, setEditorVersion] = useState(0);
  const [hasChanges, setHasChanges] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [improvementComments, setImprovementComments] = useState<
    string | undefined
  >();

  // This is a little bit of a hack, but works correctly and is simple. We have a slightly different experience
  // when creating a new prompt vs improving an existing one.
  const isCreateMode = promptMarkdown.trim() === '';

  const [createPrompt, { loading: creating }] =
    useCreateDocumentSummaryPromptMutation({
      onCompleted: (data) => {
        showFeedback('Successfully duplicated prompt', { variant: 'success' });
        navigate(ROUTE_KEYS.ADMIN_PROMPT_ITERATION, {
          id: data.createDocumentSummaryPrompt.id,
        });
      },
      onError: (err) => {
        showFeedback('Failed to duplicate prompt');
        reportError('Failed to duplicate prompt', err);
      },
    });

  const [updatePrompt, { loading: updating }] =
    useUpdateDocumentSummaryPromptMutation({
      onCompleted: () => {
        showFeedback('Successfully saved changes', { variant: 'success' });
        setHasChanges(false);
      },
      onError: (err) => {
        showFeedback('Failed to save changes');
        reportError('Failed to save prompt changes', err);
      },
    });

  const [deletePrompt, { loading: deleting }] =
    useDeleteDocumentSummaryPromptMutation({
      onCompleted: () => {
        showFeedback('Successfully deleted prompt', { variant: 'success' });
        navigate(ROUTE_KEYS.ADMIN_SUMMARY_PROMPTS, {});
      },
      onError: (err) => {
        showFeedback('Failed to delete prompt');
        reportError('Failed to delete prompt', err);
      },
    });

  useImprovePromptStreamingSubscription({
    variables: {
      input: {
        currentPrompt: promptMarkdown,
        documentType: originalPrompt?.documentType as unknown as DocumentType,
        comments: improvementComments,
      },
    },
    skip: !isStreaming || !originalPrompt,
    onData: ({ data }) => {
      const chunk = data.data?.improvePromptStreaming;
      if (chunk) {
        setSuggestedMarkdown((prev) => (prev || '') + chunk);
      }
    },
    onComplete: () => {
      // For create mode, auto-accept before clearing streaming state to avoid diff view flash
      if (isCreateMode) {
        handleAcceptSuggested();
      }
      setIsStreaming(false);
    },
    onError: (err) => {
      setIsStreaming(false);
      setIsDiffModalOpen(false);
      setSuggestedMarkdown(null);
      showFeedback('Failed to improve prompt');
      reportError('Failed to improve prompt', err);
    },
  });

  const {
    selectedDocumentId,
    setSelectedDocumentId,
    previewMarkdown,
    previewResetKey,
    generatePreview,
    isGenerating,
    isDisabled: previewDisabled,
  } = usePreviewRunner(promptId);

  const breadcrumbs = useMemo(
    () => [
      {
        label: 'Summary prompts',
        to: getCompletePathFromRouteKey(ROUTE_KEYS.ADMIN_SUMMARY_PROMPTS, {}),
      },
      {
        label: originalPrompt?.displayName ?? 'Loading...',
      },
    ],
    [originalPrompt?.displayName]
  );

  const handleDisplayNameChange = useCallback(
    (name: string) => {
      setDisplayName(name);
      setHasChanges(
        name !== originalPrompt?.displayName ||
          promptMarkdown !== (originalPrompt?.promptMarkdown ?? '')
      );
    },
    [originalPrompt, promptMarkdown]
  );

  const handleEditorUpdate = useCallback(
    (markdown: string) => {
      setPromptMarkdown(markdown);
      setHasChanges(
        markdown !== (originalPrompt?.promptMarkdown ?? '') ||
          displayName !== originalPrompt?.displayName
      );
    },
    [originalPrompt, displayName]
  );

  const handleDuplicate = useCallback(async () => {
    if (!originalPrompt) return;

    await createPrompt({
      variables: {
        input: {
          create: {
            displayName: `${originalPrompt.displayName} (copy)`,
            documentType: originalPrompt.documentType,
            isDefaultForDocType: false,
            isSystemPrompt: false,
            promptMarkdown: originalPrompt.promptMarkdown ?? '',
          },
        },
      },
    });
  }, [originalPrompt, createPrompt]);

  const handleSave = useCallback(async () => {
    if (!originalPrompt) return;

    await updatePrompt({
      variables: {
        input: {
          id: originalPrompt.id,
          update: {
            displayName,
            promptMarkdown,
          },
        },
      },
    });
  }, [originalPrompt, updatePrompt, displayName, promptMarkdown]);

  const handleDelete = useCallback(async () => {
    if (!originalPrompt) return;

    await deletePrompt({
      variables: {
        id: originalPrompt.id,
      },
    });
  }, [originalPrompt, deletePrompt]);

  const handleAcceptSuggested = useCallback(() => {
    if (!suggestedMarkdown) return;
    setPromptMarkdown(suggestedMarkdown);
    setEditorVersion((v) => v + 1);
    setHasChanges(true);
    setIsDiffModalOpen(false);
    setSuggestedMarkdown(null);
    showFeedback('Successfully applied suggested changes', {
      variant: 'success',
    });
  }, [suggestedMarkdown, showFeedback]);

  const handleKeepCurrent = useCallback(() => {
    setIsDiffModalOpen(false);
    setSuggestedMarkdown(null);
    setIsStreaming(false);
  }, []);

  const handleOpenImprovePromptModal = useCallback(() => {
    setIsImprovePromptModalOpen(true);
  }, []);

  const handleImprovePrompt = useCallback(
    async (comments?: string) => {
      if (!originalPrompt) return;
      setIsImprovePromptModalOpen(false);
      setImprovementComments(comments);
      setSuggestedMarkdown('');
      setIsDiffModalOpen(true);
      setIsStreaming(true);
    },
    [originalPrompt]
  );

  const handleGeneratePreview = useCallback(() => {
    if (!originalPrompt) return;
    generatePreview(promptMarkdown, originalPrompt.id);
  }, [generatePreview, promptMarkdown, originalPrompt]);

  const isSystemPrompt = originalPrompt?.isSystemPrompt;
  const readOnly = useMemo(
    () => isSystemPrompt || isStreaming || isGenerating,
    [isSystemPrompt, isStreaming, isGenerating]
  );

  const documentDisplayType =
    DOCUMENT_TYPE_DISPLAY_NAMES[originalPrompt.documentType];

  // Show the diff modal during streaming (so user sees generation progress),
  // but hide it for create mode once streaming completes (no diff to compare).
  // For improve mode, always show after streaming so user can review changes.
  const hasSuggestedContent = isDiffModalOpen && suggestedMarkdown != null;
  const showDiffModal = hasSuggestedContent && (isStreaming || !isCreateMode);

  return (
    <>
      {isImprovePromptModalOpen && (
        <ImprovePromptModal
          isOpen={isImprovePromptModalOpen}
          onClose={() => setIsImprovePromptModalOpen(false)}
          onImprove={handleImprovePrompt}
          isCreate={isCreateMode}
        />
      )}
      {showDiffModal && (
        <PromptDiffModal
          isOpen={isDiffModalOpen}
          currentText={promptMarkdown}
          suggestedText={suggestedMarkdown}
          onAcceptSuggestedPrompt={handleAcceptSuggested}
          onRejectSuggestedPrompt={handleKeepCurrent}
          isStreaming={isStreaming}
          isCreate={isCreateMode}
        />
      )}
      <SubpageLayout
        heading={<BigBreadcrumbs crumbs={breadcrumbs} />}
        actions={
          <PageActions
            promptId={originalPrompt.id}
            shouldAutoRun={originalPrompt.shouldAutoRun}
            isDefaultForDocType={originalPrompt.isDefaultForDocType}
            documentDisplayType={documentDisplayType}
            isSystemPrompt={originalPrompt.isSystemPrompt}
            useExtendedThinkingModelForPrompt={
              originalPrompt.useExtendedThinkingModal ?? false
            }
            onDelete={handleDelete}
            deleting={deleting}
          />
        }
        classes={{ childrenWrapper: classes.childrenWrapper }}
      >
        <Stack spacing={2} height={'calc(100vh - 160px)'}>
          <Stack direction="row" spacing={2} sx={{ flex: 1, minHeight: 0 }}>
            <Box sx={{ flex: 1 }}>
              <PromptEditorPanel
                originalPrompt={originalPrompt}
                displayName={displayName}
                promptMarkdown={promptMarkdown}
                onDisplayNameChange={handleDisplayNameChange}
                onPromptMarkdownChange={handleEditorUpdate}
                readOnly={readOnly}
                editorResetKey={editorVersion}
                onDuplicate={handleDuplicate}
                onOpenImproveModal={handleOpenImprovePromptModal}
                onSave={handleSave}
                duplicating={creating}
                improving={isStreaming}
                saving={updating}
                hasChanges={hasChanges}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <PreviewPanel
                selectedDocumentId={selectedDocumentId}
                documentType={
                  originalPrompt.documentType as unknown as DocumentType
                }
                onSelectDocument={setSelectedDocumentId}
                onGeneratePreview={handleGeneratePreview}
                previewMarkdown={previewMarkdown}
                previewResetKey={previewResetKey}
                isGenerating={isGenerating}
                isDisabled={previewDisabled || isStreaming}
              />
            </Box>
          </Stack>
        </Stack>
      </SubpageLayout>
    </>
  );
}
